import { Field as BaseField } from "@base-ui/react/field";
import clsx from "clsx";
import type { ComponentProps, KeyboardEvent, Ref } from "react";
import { FieldFooter, FieldLabel, type FieldLabelProps } from "../Field/FieldParts";
import styles from "../Field/Field.module.css";

export interface TextAreaProps
  extends FieldLabelProps, Omit<ComponentProps<"textarea">, "className" | "style" | "children" | "ref"> {
  onValueChange?: (value: string) => void;
  /** Cmd/Ctrl+Enter submits the surrounding form (guideline for textareas). */
  submitOnModEnter?: boolean;
  /** Monospace, for pasted formulas, CSV and JSON. */
  mono?: boolean;
  className?: string;
  textareaRef?: Ref<HTMLTextAreaElement>;
}

/** A labelled multi-line text input (notes, pasted formulas, JSON payloads). */
export function TextArea({
  label,
  hideLabel,
  description,
  error,
  optional,
  disabled,
  className,
  textareaRef,
  onValueChange,
  submitOnModEnter = true,
  mono = false,
  name,
  rows = 4,
  onKeyDown,
  ...textareaProps
}: TextAreaProps) {
  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    onKeyDown?.(event);
    if (submitOnModEnter && event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.currentTarget.form?.requestSubmit();
    }
  }
  return (
    <BaseField.Root
      name={name}
      disabled={disabled}
      invalid={error ? true : undefined}
      className={clsx(styles.field, className)}
    >
      <FieldLabel label={label} hideLabel={hideLabel} optional={optional} />
      <div className={styles.control}>
        <BaseField.Control
          name={name}
          className={clsx(styles.input, styles.textarea)}
          data-mono={mono || undefined}
          onValueChange={onValueChange ? (value) => onValueChange(value) : undefined}
          render={<textarea {...textareaProps} ref={textareaRef} rows={rows} onKeyDown={handleKeyDown} />}
        />
      </div>
      <FieldFooter description={description} error={error} />
    </BaseField.Root>
  );
}
